// Executive Explain — shows the exact records and signal weights that
// produced a recommendation. Answers "Why is this High Priority?" using
// real data, not generic language.
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import {
  ExecRecommendation,
  explainRecommendation,
  priorityBadgeClass,
} from "@/lib/executive/engine";

export function ExplainDialog({
  open,
  onOpenChange,
  recommendation,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  recommendation: ExecRecommendation;
}) {
  const [records, setRecords] = useState<Array<{ table: string; id: string; data: any }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    explainRecommendation(recommendation.id)
      .then((r) => {
        if (!cancelled) setRecords(r.records);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, recommendation.id]);

  const { signals, weights, confidence } = recommendation.reasoning ?? ({} as any);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge className={priorityBadgeClass(recommendation.priority)}>
              {recommendation.priority.toUpperCase()}
            </Badge>
            Why this is flagged
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm max-h-[60vh] overflow-y-auto">
          <div>
            <div className="font-medium">{recommendation.title}</div>
            {recommendation.summary && (
              <div className="text-muted-foreground mt-1">{recommendation.summary}</div>
            )}
          </div>

          <section>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Rule
            </div>
            <div className="font-mono text-xs">{recommendation.rule_id}</div>
          </section>

          {signals && (
            <section>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Signals detected
              </div>
              <pre className="rounded bg-muted p-2 text-xs overflow-x-auto">
                {JSON.stringify(signals, null, 2)}
              </pre>
            </section>
          )}

          {weights && (
            <section>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Priority score breakdown (total {recommendation.score.toFixed(1)})
              </div>
              <ul className="text-xs space-y-0.5">
                {Object.entries(weights).map(([k, v]) => (
                  <li key={k} className="flex justify-between">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-mono">{Number(v).toFixed(1)}</span>
                  </li>
                ))}
              </ul>
              {typeof confidence === "number" && (
                <div className="text-xs text-muted-foreground mt-1">
                  Confidence: {(confidence * 100).toFixed(0)}%
                </div>
              )}
            </section>
          )}

          <section>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Records used {loading && <Loader2 className="inline h-3 w-3 animate-spin" />}
            </div>
            {records.length === 0 && !loading && (
              <div className="text-xs text-muted-foreground">No linked records.</div>
            )}
            <ul className="space-y-2">
              {records.map((r) => (
                <li key={`${r.table}:${r.id}`} className="rounded border p-2 text-xs">
                  <div className="font-mono text-muted-foreground">
                    {r.table} • {r.id}
                  </div>
                  <div className="mt-1">
                    {r.data?.title ||
                      r.data?.full_name ||
                      r.data?.person_name ||
                      r.data?.proposed_class_name ||
                      r.data?.reason ||
                      "(record)"}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
